import { useState } from "react";
import {
  Alert,
  Box,
  CheckBox,
  Grid,
  Modal,
  ModalContent,
  TextInput
} from "@dealer-center/core-components";
import { useCreateDepartments } from "@dealer-center/ddc-pages-departments-fetch";
import { defaultDepartments } from "./defaultDepartments";
import { labelToType, validateDepartment } from "./utils";

import type { AddDepartmentModal } from "../Departments";
import type { CreateDepartmentsData } from "@dealer-center/ddc-pages-departments-interface";

export interface AddDepartmentProps extends AddDepartmentModal {
  hideModal: () => void;
  departments: CreateDepartmentsData;
  accountId: string;
  userId: string | undefined;
}

export const AddDepartment = ({
  hideModal,
  modalToShow,
  departments,
  accountId,
  userId
}: AddDepartmentProps) => {
  const mutation = useCreateDepartments();
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [customDepartmentLabel, setCustomDepartmentLabel] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const customDepartmentType = customDepartmentLabel
    ? labelToType(customDepartmentLabel)
    : "";

  // Get existing department types for validation
  const existingTypes = departments.map(d => d.type.toUpperCase());
  const defaultDepartmentTypes = defaultDepartments.map(d =>
    d.value.toUpperCase()
  );

  const existingCustomDepartments = departments.filter(
    dept => !defaultDepartmentTypes.includes(dept.type.toUpperCase())
  );

  const hasValidationError = (): boolean => {
    if (!customDepartmentLabel) return false;

    const validationFn = validateDepartment(
      existingTypes,
      defaultDepartmentTypes
    );

    const validation = validationFn("", customDepartmentLabel);
    return validation.triggered && !validation.validationState.isValid;
  };

  const handleAddDepartments = () => {
    if (mutation.isPending) {
      return;
    }

    setErrorMsg(null); // Clear previous errors

    const selectedDefaultDepts =
      defaultDepartments.reduce<CreateDepartmentsData>((acc, dept) => {
        if (selectedDepartments.includes(dept.value)) {
          acc.push({
            label: dept.label,
            type: dept.value,
            accountId: accountId
          });
        }
        return acc;
      }, []);

    const customDept = customDepartmentType
      ? [
          {
            label: customDepartmentLabel.trim(),
            type: customDepartmentType,
            accountId: accountId
          }
        ]
      : [];

    const departmentsToAdd: CreateDepartmentsData = [
      ...selectedDefaultDepts,
      ...customDept
    ];

    if (departmentsToAdd.length === 0) {
      return;
    }

    mutation
      .mutateAsync({
        accountId: accountId,
        departments: departmentsToAdd,
        userId: userId
      })
      .then(() => {
        hideModal();
      })
      .catch(error => {
        console.error("Failed to create departments:", error);
        setErrorMsg(
          error.message || "Failed to create departments. Please try again."
        );
      });
  };
  return (
    <Modal
      footer={{
        primary: {
          label: "Add",
          disabled:
            mutation.isPending ||
            (selectedDepartments.length === 0 && !customDepartmentType) ||
            hasValidationError(),
          action: handleAddDepartments,
          isLoading: mutation.isPending
        },
        options: {
          label: "Cancel",
          action: () => hideModal(),
          disabled: mutation.isPending
        }
      }}
      header="Add Department"
      show={modalToShow === "add"}
      size="large"
      onHide={() => !mutation.isPending && hideModal()}
    >
      <ModalContent>
        {errorMsg && (
          <Alert sx={{ mb: 2 }} type="error">
            {errorMsg}
          </Alert>
        )}
        <Alert role="status" sx={{ mb: 2 }} type="info">
          Note: Custom department names can contain letters, numbers,
          apostrophes, and spaces.
        </Alert>
        {/* Default Departments Section */}
        <Box sx={{ mb: 2, fontWeight: "bold" }}>Pre-configured Departments</Box>
        <Grid container sx={{ mb: 4, rowGap: 1 }}>
          {defaultDepartments.map(item => {
            const isExisting = existingTypes.includes(item.value.toUpperCase());
            return (
              <Grid key={item.value} size={{ sm: 6 }}>
                <CheckBox
                  checked={selectedDepartments.includes(item.value)}
                  disabled={mutation.isPending || isExisting}
                  label={item.label}
                  name={item.value}
                  value={item.value}
                  onChange={e => {
                    const { checked, checkboxValue } = e.target.value;
                    if (checked) {
                      setSelectedDepartments(selected => [
                        ...selected,
                        checkboxValue
                      ]);
                    } else {
                      setSelectedDepartments(selected =>
                        selected.filter(
                          selectedDepartment =>
                            selectedDepartment !== checkboxValue
                        )
                      );
                    }
                  }}
                />
              </Grid>
            );
          })}
        </Grid>

        {/* Existing Custom Departments (Display Only) */}
        {existingCustomDepartments.length > 0 && (
          <>
            <Box sx={{ mb: 2, fontWeight: "bold" }}>
              Existing Custom Departments
            </Box>
            <Box
              sx={{
                mb: 4,
                p: 2,
                bgcolor: "grey.100",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.300"
              }}
            >
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1
                }}
              >
                {existingCustomDepartments.map(dept => (
                  <Box
                    component="li"
                    key={dept.type}
                    sx={{ color: "text.secondary" }}
                  >
                    <strong>{dept.label}</strong> ({dept.type})
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}

        {/* Custom Department Input */}
        <Box sx={{ mb: 2, fontWeight: "bold" }}>Add Custom Department</Box>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={{ sm: 12 }}>
            <TextInput
              disabled={mutation.isPending}
              getError={validateDepartment(
                existingTypes,
                defaultDepartmentTypes
              )}
              id="customDepartmentLabel"
              label="Custom Department Label"
              name="customDepartmentLabel"
              value={customDepartmentLabel}
              onChange={e => setCustomDepartmentLabel(e.target.value)}
            />
            {customDepartmentLabel && (
              <Box
                sx={{
                  mt: 1,
                  fontSize: "0.875rem",
                  color: "text.secondary",
                  wordBreak: "break-word",
                  overflowWrap: "break-word"
                }}
              >
                Type will be: <strong>{customDepartmentType}</strong>
              </Box>
            )}
          </Grid>
        </Grid>
      </ModalContent>
    </Modal>
  );
};
